import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import ClientDialog from "../components/ClientDialog";
import NearbyMapPanel from "../components/NearbyMapPanel";
import { Plus, Search, Users as UsersIcon, TrendingUp, AlertCircle, CalendarClock, Phone, MapPin, ArrowRight, Flame, BookUser, Mail } from "lucide-react";

const PRIORITY_META = {
  high: { label: "High", cls: "bg-red-100 text-red-700 border-red-200", icon: Flame },
  medium: { label: "Medium", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: TrendingUp },
  low: { label: "Low", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: UsersIcon },
};

const LEAD_META = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  unqualified: "Unqualified",
  converted: "Converted",
};

function StatCard({ label, value, icon: Icon, accent = "blue", onClick, testId }) {
  const border =
    accent === "red" ? "border-l-red-500" :
    accent === "amber" ? "border-l-amber-400" :
    accent === "emerald" ? "border-l-emerald-600" :
    accent === "slate" ? "border-l-slate-400" :
    "border-l-blue-800";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left bg-white border ${border} border-l-4 border-y border-r border-slate-200 rounded-r-md p-5 shadow-sm hover:border-blue-800 transition-colors`}
    >
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
        <Icon size={20} className="text-slate-400" strokeWidth={2} />
      </div>
      <div className="mt-3 font-display text-4xl font-black tracking-tighter text-slate-900">{value}</div>
    </button>
  );
}

function PriorityBadge({ priority }) {
  const meta = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

export default function SalesPage() {
  const { user, isManager } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [reps, setReps] = useState([]);
  const [query, setQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [repFilter, setRepFilter] = useState("all");
  const [tab, setTab] = useState("all"); // all | leads | followups | phonebook
  const [newOpen, setNewOpen] = useState(false);

  const isAdmin = !!isManager;

  const load = async () => {
    try {
      const [{ data: cl }, { data: db }] = await Promise.all([
        api.get("/sales/clients"),
        api.get("/sales/dashboard"),
      ]);
      setClients(cl);
      setDashboard(db);
      if (isAdmin) {
        const { data: u } = await api.get("/users").catch(() => ({ data: [] }));
        setReps((u || []).filter((x) => x.role === "sales_representative"));
      }
    } catch (err) {
      toast.error("Could not load sales data");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      if (priorityFilter !== "all" && c.priority !== priorityFilter) return false;
      if (repFilter !== "all" && c.sales_rep_id !== repFilter) return false;
      if (tab === "leads" && !["new", "contacted", "qualified"].includes(c.lead_status)) return false;
      if (tab === "followups") {
        const today = new Date().toISOString().slice(0, 10);
        if (!c.next_followup_date || c.next_followup_date > today) return false;
      }
      const q = query.trim().toLowerCase();
      if (q) {
        return (
          c.company_name?.toLowerCase().includes(q) ||
          c.contact_name?.toLowerCase().includes(q) ||
          c.city?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [clients, priorityFilter, repFilter, tab, query]);

  return (
    <div>
      <PageHeader
        eyebrow="Pipeline"
        title="Sales"
        description={isAdmin ? "Every client, every rep — the full pipeline in one place." : "Your clients, follow-ups, and opportunities."}
        testId="sales-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <Button
              onClick={() => setNewOpen(true)}
              data-testid="new-client-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              <Plus size={16} className="mr-1.5" /> New Client
            </Button>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-6">
        {/* Dashboard tiles */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sales-stats">
            <StatCard label="My Clients" value={dashboard.total_clients} icon={UsersIcon} onClick={() => { setTab("all"); setPriorityFilter("all"); }} testId="stat-all" />
            <StatCard label="Leads" value={dashboard.leads} icon={TrendingUp} onClick={() => setTab("leads")} testId="stat-leads" accent="amber" />
            <StatCard label="High Priority" value={dashboard.high_priority} icon={Flame} onClick={() => setPriorityFilter("high")} testId="stat-high" accent="red" />
            <StatCard label="Medium" value={dashboard.medium_priority} icon={TrendingUp} onClick={() => setPriorityFilter("medium")} testId="stat-medium" accent="amber" />
            <StatCard label="Low" value={dashboard.low_priority} icon={UsersIcon} onClick={() => setPriorityFilter("low")} testId="stat-low" accent="slate" />
            <StatCard label="Follow-ups Due" value={dashboard.followup_due} icon={AlertCircle} onClick={() => setTab("followups")} testId="stat-followups" accent="red" />
          </div>
        )}

        {/* Filters row */}
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search clients, contacts, cities…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="sales-search-input"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="rounded-sm bg-slate-100">
              <TabsTrigger value="all" data-testid="tab-all" className="rounded-sm">All</TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads" className="rounded-sm">Leads</TabsTrigger>
              <TabsTrigger value="followups" data-testid="tab-followups" className="rounded-sm">Follow-ups</TabsTrigger>
              <TabsTrigger value="phonebook" data-testid="tab-phonebook" className="rounded-sm">Phone Book</TabsTrigger>
              <TabsTrigger value="nearby" data-testid="tab-nearby" className="rounded-sm">Nearby</TabsTrigger>
            </TabsList>
          </Tabs>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger data-testid="filter-priority" className="w-40 rounded-sm">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && reps.length > 0 && (
            <Select value={repFilter} onValueChange={setRepFilter}>
              <SelectTrigger data-testid="filter-rep" className="w-52 rounded-sm">
                <SelectValue placeholder="Sales rep" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All reps</SelectItem>
                {reps.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Phone Book — dedicated tab */}
        {tab === "nearby" ? (
          <div className="mt-6"><NearbyMapPanel /></div>
        ) : tab === "phonebook" ? (
          <div className="mt-6 border border-slate-200 rounded-md bg-white shadow-sm" data-testid="phonebook-panel">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <BookUser size={16} className="text-slate-500" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Phone Book</div>
              <span className="text-xs font-mono text-slate-500">· {filtered.length} clients</span>
            </div>
            {filtered.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No contacts match this search.</div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {filtered.map((c) => (
                  <li key={c.id} className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`phonebook-row-${c.id}`}>
                    <Link to={`/sales/clients/${c.id}`} className="min-w-0 flex-1 hover:text-blue-800">
                      <div className="font-semibold text-slate-900 truncate">{c.company_name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.contact_name || "—"}{c.city && ` · ${c.city}, ${c.state}`}
                      </div>
                    </Link>
                    <div className="flex items-center gap-3 shrink-0">
                      {c.phone && (
                        <a href={`tel:${c.phone}`} data-testid={`phonebook-call-${c.id}`} className="text-sm font-mono text-slate-800 hover:text-blue-800 inline-flex items-center gap-1.5">
                          <Phone size={14} className="text-slate-400" /> {c.phone}
                        </a>
                      )}
                      {c.email && (
                        <a href={`mailto:${c.email}`} data-testid={`phonebook-email-${c.id}`} className="text-sm text-slate-800 hover:text-blue-800 inline-flex items-center gap-1.5">
                          <Mail size={14} className="text-slate-400" /> <span className="hidden md:inline">{c.email}</span>
                        </a>
                      )}
                      <PriorityBadge priority={c.priority} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
        /* Table */
        <div className="mt-6 border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
          <table className="w-full text-sm min-w-[880px]" data-testid="clients-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="p-4">Client</th>
                <th className="p-4">Contact</th>
                <th className="p-4">Location</th>
                <th className="p-4">Priority</th>
                <th className="p-4">Lead status</th>
                <th className="p-4">Follow-up</th>
                <th className="p-4 text-right">Est. value</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-slate-500 font-mono text-sm">
                    No clients match these filters yet.
                  </td>
                </tr>
              ) : filtered.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/sales/clients/${c.id}`)}
                  className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors cursor-pointer"
                  data-testid={`client-row-${c.id}`}
                >
                  <td className="p-4">
                    <div className="font-semibold text-slate-900">{c.company_name}</div>
                    {c.customer_type && <div className="text-xs text-slate-500 mt-0.5 capitalize">{c.customer_type}</div>}
                  </td>
                  <td className="p-4">
                    <div className="text-slate-800">{c.contact_name || "—"}</div>
                    {c.phone && <div className="text-xs font-mono text-slate-500 mt-0.5 inline-flex items-center gap-1"><Phone size={11} /> {c.phone}</div>}
                  </td>
                  <td className="p-4">
                    {c.city ? (
                      <div className="text-slate-700 inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400" /> {c.city}, {c.state}</div>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-4"><PriorityBadge priority={c.priority} /></td>
                  <td className="p-4 text-slate-700">{LEAD_META[c.lead_status] || c.lead_status}</td>
                  <td className="p-4 font-mono text-xs text-slate-500">{c.next_followup_date || "—"}</td>
                  <td className="p-4 text-right font-mono font-semibold text-slate-900">
                    {c.estimated_value ? `$${Number(c.estimated_value).toLocaleString()}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Right column widgets */}
        {dashboard && (dashboard.upcoming_followups?.length || dashboard.recent_contacts?.length) && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-md bg-white shadow-sm p-4" data-testid="upcoming-followups-panel">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 inline-flex items-center gap-2">
                <CalendarClock size={14} /> Upcoming follow-ups
              </div>
              {dashboard.upcoming_followups.length === 0 ? (
                <div className="text-sm text-slate-500">Nothing scheduled.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {dashboard.upcoming_followups.map((c) => (
                    <li key={c.id} className="py-2 flex items-center justify-between">
                      <Link to={`/sales/clients/${c.id}`} className="font-semibold text-slate-800 hover:text-blue-800">{c.company_name}</Link>
                      <span className="font-mono text-xs text-slate-500">{c.next_followup_date}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border border-slate-200 rounded-md bg-white shadow-sm p-4" data-testid="recent-contacts-panel">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3 inline-flex items-center gap-2">
                <ArrowRight size={14} /> Recently contacted
              </div>
              {dashboard.recent_contacts.length === 0 ? (
                <div className="text-sm text-slate-500">No recent contact yet.</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {dashboard.recent_contacts.map((c) => (
                    <li key={c.id} className="py-2 flex items-center justify-between">
                      <Link to={`/sales/clients/${c.id}`} className="font-semibold text-slate-800 hover:text-blue-800">{c.company_name}</Link>
                      <span className="font-mono text-xs text-slate-500">{c.last_contact_date}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>

      <ClientDialog open={newOpen} onOpenChange={setNewOpen} reps={reps} onSuccess={load} />
    </div>
  );
}
